/**
 * Agent runs panel API client (#169 slice 3), backend `project_router`.
 *
 * Every call reads or writes GitLab under the viewer's own credential.
 * The detail route takes the definition's FILE name (`summary.file_name`,
 * unique where the stem is not); runs, Save and Withdraw take its NAME.
 * Errors stay axios errors: `utils/errors.saveErrorDetail` reads Save's
 * 409/422/503 bodies.
 */

import { api } from '@services/api';
import { API_PATHS } from '@utils/constants';
import {
  CiIncludeResponseSchema,
  DefinitionDetailSchema,
  DefinitionRunsResponseSchema,
  PanelResponseSchema,
  PreviewResponseSchema,
  RenderResponseSchema,
  SaveResponseSchema,
  WithdrawResponseSchema,
} from '../schemas/projectPanel.schemas';
import type {
  CiIncludeResponse,
  DefinitionDetail,
  DefinitionRunRow,
  PanelResponse,
  PreviewResponse,
  RunDefinition,
  SaveResponse,
  WithdrawResponse,
} from '../types/projectPanel.types';

function definitionsPath(projectId: string, segment: string): string {
  return `${API_PATHS.projectAgentRuns(projectId)}/definitions/${encodeURIComponent(segment)}`;
}

export async function getPanel(projectId: string): Promise<PanelResponse> {
  const response = await api.get(API_PATHS.projectAgentRuns(projectId));
  return PanelResponseSchema.parse(response.data);
}

/**
 * Opens Sanctum's MR, as the viewer, that adds the agent component to the
 * default branch's `.gitlab-ci.yml`, or answers the one already open. A 422
 * body is `{message, include_line}`: the file is one to edit by hand.
 */
export async function addCiInclude(projectId: string): Promise<CiIncludeResponse> {
  const response = await api.post(`${API_PATHS.projectAgentRuns(projectId)}/ci-include`);
  return CiIncludeResponseSchema.parse(response.data);
}

export async function setPaused(projectId: string, paused: boolean): Promise<PanelResponse> {
  const response = await api.put(`${API_PATHS.projectAgentRuns(projectId)}/paused`, { paused });
  return PanelResponseSchema.parse(response.data);
}

export async function getDefinition(projectId: string, fileName: string): Promise<DefinitionDetail> {
  const response = await api.get(definitionsPath(projectId, fileName));
  return DefinitionDetailSchema.parse(response.data);
}

/** At most 50 rows, newest first: runs and matches that dispatched nothing. */
export async function getDefinitionRuns(
  projectId: string,
  name: string,
): Promise<DefinitionRunRow[]> {
  const response = await api.get(`${definitionsPath(projectId, name)}/runs`);
  return DefinitionRunsResponseSchema.parse(response.data).rows;
}

/** Lints `content` with the default branch's other definitions. */
export async function previewDefinition(
  projectId: string,
  name: string,
  content: string,
): Promise<PreviewResponse> {
  const response = await api.post(`${API_PATHS.projectAgentRuns(projectId)}/definitions/preview`, {
    name,
    content,
  });
  return PreviewResponseSchema.parse(response.data);
}

/** The form's definition as YAML. Pure server-side: no GitLab call. */
export async function renderDefinition(
  projectId: string,
  definition: RunDefinition,
): Promise<string> {
  const response = await api.post(`${API_PATHS.projectAgentRuns(projectId)}/definitions/render`, {
    definition,
  });
  return RenderResponseSchema.parse(response.data).content;
}

/**
 * Commits `content` to `sanctum-def/<name>` and opens or reuses its MR.
 * `baseContent` is the text the editor started from (the detail's
 * `mr_content` when `sanctum_mr` is set, else `content`), null for a new
 * definition; Save answers 409 when the file is no longer that text.
 */
export async function saveDefinition(
  projectId: string,
  name: string,
  content: string,
  baseContent: string | null,
): Promise<SaveResponse> {
  const response = await api.put(definitionsPath(projectId, name), {
    content,
    base_content: baseContent,
  });
  return SaveResponseSchema.parse(response.data);
}

/**
 * Closes Sanctum's `sanctum-def/<name>` MR `iid`, the one the viewer
 * confirmed, and deletes its branch. The backend answers 409 when
 * Sanctum's open MR is now another one.
 */
export async function withdrawDefinition(
  projectId: string,
  name: string,
  iid: number,
): Promise<WithdrawResponse> {
  const response = await api.post(`${definitionsPath(projectId, name)}/withdraw`, { iid });
  return WithdrawResponseSchema.parse(response.data);
}
